/**
 * backlineProvider.ts — effective per-item backline provider resolution.
 *
 * A backline item (BacklineItem) can be provided by the band (default) or the
 * venue. The EFFECTIVE provider for a given show is resolved in this order:
 *
 *   1. Per-show override — `riders.requirements.backlineProviders[itemId]`
 *      (a Record<itemId, 'band'|'venue'>). Wins when it is a known value.
 *   2. Transport — when the show travels by plane or train the band cannot
 *      haul its own backline, so the venue provides it.
 *   3. The card default — `item.providedBy` (defaults to 'band').
 *
 * This MUST stay in lock-step with the server-side mirror
 * `server/lib/backline-provider.js` (the promoter share `buildItems` runs in
 * Node/CommonJS and cannot import this TS module). Keep the logic identical.
 */
import type { ProvidedBy } from '../types';

/** True when this transport can't carry the band's own backline. */
export function isVenueForcedTransport(transport: string | undefined | null): boolean {
  return transport === 'plane' || transport === 'train';
}

/**
 * Effective provider for one backline item on one show.
 *
 * @param item        the backline item (its `providedBy` is the card default).
 * @param transport   the show's transport ('van' | 'plane' | 'tourbus' | 'train').
 * @param override    the per-show override value for this item id, if any
 *                    (`riders.requirements.backlineProviders[item.id]`).
 */
export function resolveBacklineProvider(
  item: { providedBy?: ProvidedBy } | null | undefined,
  transport: string | undefined | null,
  override?: ProvidedBy | null,
): ProvidedBy {
  if (override === 'band' || override === 'venue') return override;
  if (isVenueForcedTransport(transport)) return 'venue';
  return item?.providedBy === 'venue' ? 'venue' : 'band';
}

/**
 * Read the per-show override for one item id from a rider's `requirements`.
 * `requirements` may be a parsed object, a JSON string (the riders table stores
 * it as TEXT), or null/undefined for a band-level (non-show) rider preview.
 */
export function backlineOverrideFor(
  requirements: Record<string, any> | string | null | undefined,
  itemId: string,
): ProvidedBy | undefined {
  let req: Record<string, any> | null = null;
  if (typeof requirements === 'string') {
    try { req = JSON.parse(requirements); } catch { req = null; }
  } else {
    req = requirements || null;
  }
  const map = req?.backlineProviders;
  if (!map || typeof map !== 'object') return undefined;
  const v = map[itemId];
  return v === 'band' || v === 'venue' ? v : undefined;
}